import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Hands } from '@mediapipe/hands';
import { Camera } from '@mediapipe/camera_utils';
import './GestureControl.css';

// Global singleton - persists across React hot reloads via window object
if (!window.__MEDIAPIPE_HANDS_INSTANCE__) {
  window.__MEDIAPIPE_HANDS_INSTANCE__ = null;
  window.__MEDIAPIPE_RESULTS_LISTENER__ = null;
}

// Hard Singleton with explicit asset mapping
const getHandsInstance = () => {
  if (window.__MEDIAPIPE_HANDS_INSTANCE__) {
    return window.__MEDIAPIPE_HANDS_INSTANCE__;
  }

  const hands = new Hands({
    locateFile: (file) => {
      return `https://cdn.jsdelivr.net/npm/@mediapipe/hands@0.4.1646424915/${file}`;
    }
  });

  hands.setOptions({
    maxNumHands: 1,
    modelComplexity: 1,
    minDetectionConfidence: 0.7,
    minTrackingConfidence: 0.7
  });

  // Register the one-and-only bridge listener
  hands.onResults((results) => {
    if (window.__MEDIAPIPE_RESULTS_LISTENER__) {
      window.__MEDIAPIPE_RESULTS_LISTENER__(results);
    }
  });

  window.__MEDIAPIPE_HANDS_INSTANCE__ = hands;
  return hands;
};

const OPPOSITE_GESTURES = {
  'swipe_up': 'swipe_down',
  'swipe_down': 'swipe_up',
  'swipe_left': 'swipe_right',
  'swipe_right': 'swipe_left'
};

// Tunable gesture robustness settings
const STABILITY_WINDOW = 3; // Very small window for immediate response
const STABILITY_MIN_COUNT = 2; // Only need 2 matching frames
const SMOOTHING_ALPHA = 0.6; // higher = heavier smoothing
const FIST_HOLD_MS = 2000; // Increased to 2s to prevent accidental disable
const STATIC_GESTURE_MAX_SPEED = 0.08;

const GestureControl = ({ onGesture, enabled, onEnabledChange }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [swipeThreshold, setSwipeThreshold] = useState(0.1);
  const [debounceMs, setDebounceMs] = useState(700);
  const [minConfidence, setMinConfidence] = useState(0.55);
  const swipeThresholdRef = useRef(swipeThreshold);
  const debounceMsRef = useRef(debounceMs);
  const minConfidenceRef = useRef(minConfidence);
  const cameraRef = useRef(null);
  const lastGestureRef = useRef({ gesture: '', time: 0 });
  const onGestureRef = useRef(onGesture);
  const previousHandsRef = useRef([]);
  const smoothedLandmarksRef = useRef(null);
  const gestureHistoryRef = useRef([]);
  const fistHoldRef = useRef({ start: 0, emitted: false });

  useEffect(() => {
    onGestureRef.current = onGesture;
  }, [onGesture]);

  useEffect(() => {
    swipeThresholdRef.current = swipeThreshold;
  }, [swipeThreshold]);

  useEffect(() => {
    debounceMsRef.current = debounceMs;
  }, [debounceMs]);

  useEffect(() => {
    minConfidenceRef.current = minConfidence;
  }, [minConfidence]);


  const previousLandmarksRef = useRef(null);
  const movementCooldownRef = useRef(0);

  const drawHandLandmarks = useCallback((ctx, landmarks) => {
    const canvasWidth = 320;
    const canvasHeight = 240;

    const connections = [
      [0, 1], [1, 2], [2, 3], [3, 4], // Thumb
      [0, 5], [5, 6], [6, 7], [7, 8], // Index
      [0, 9], [9, 10], [10, 11], [11, 12], // Middle
      [0, 13], [13, 14], [14, 15], [15, 16], // Ring
      [0, 17], [17, 18], [18, 19], [19, 20], // Pinky
      [5, 9], [9, 13], [13, 17] // Palm
    ];

    ctx.strokeStyle = '#00ff00';
    ctx.lineWidth = 2;

    connections.forEach(([start, end]) => {
      ctx.beginPath();
      ctx.moveTo(landmarks[start].x * canvasWidth, landmarks[start].y * canvasHeight);
      ctx.lineTo(landmarks[end].x * canvasWidth, landmarks[end].y * canvasHeight);
      ctx.stroke();
    });

    ctx.fillStyle = '#ff0000';
    landmarks.forEach((landmark) => {
      ctx.beginPath();
      ctx.arc(landmark.x * canvasWidth, landmark.y * canvasHeight, 4, 0, 2 * Math.PI);
      ctx.fill();
    });
  }, []);

  const mirrorLandmarks = useCallback((landmarks) => {
    return landmarks.map(point => ({
      ...point,
      x: 1 - point.x
    }));
  }, []);

  const smoothLandmarks = useCallback((landmarks) => {
    if (!smoothedLandmarksRef.current) {
      smoothedLandmarksRef.current = landmarks;
      return landmarks;
    }

    const smoothed = landmarks.map((point, i) => {
      const prev = smoothedLandmarksRef.current[i];
      return {
        x: prev.x * SMOOTHING_ALPHA + point.x * (1 - SMOOTHING_ALPHA),
        y: prev.y * SMOOTHING_ALPHA + point.y * (1 - SMOOTHING_ALPHA),
        z: prev.z * SMOOTHING_ALPHA + point.z * (1 - SMOOTHING_ALPHA),
      };
    });

    smoothedLandmarksRef.current = smoothed;
    return smoothed;
  }, []);

  const detectSwipe = useCallback((landmarks) => {
    const history = previousHandsRef.current;
    history.push({
      pos: { x: landmarks[9].x, y: landmarks[9].y },
      time: Date.now()
    });

    if (history.length > 12) {
      history.shift();
    }

    if (history.length < 4) return null;

    const first = history[0];
    const last = history[history.length - 1];

    const dx = last.pos.x - first.pos.x;
    const dy = last.pos.y - first.pos.y;
    const dt = last.time - first.time;

    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Calculate linearity (straightness of the path)
    // Sum of step distances vs total displacement
    let totalPath = 0;
    for (let i = 1; i < history.length; i++) {
      const d = Math.sqrt(
        Math.pow(history[i].pos.x - history[i - 1].pos.x, 2) +
        Math.pow(history[i].pos.y - history[i - 1].pos.y, 2)
      );
      totalPath += d;
    }
    const linearity = distance / (totalPath || 1);
    const velocity = distance / (dt || 1); // units/ms

    const threshold = swipeThresholdRef.current;

    // Robust Swipe Criteria:
    // 1. Min distance threshold
    // 2. High linearity (prevents jitter/circular motion)
    // 3. Dominant axis (prevents diagonal confusion)
    // 4. Min velocity (prevents slow hand repositioning)

    // Relaxed Swipe Criteria for better responsiveness
    if (distance > threshold && linearity > 0.6 && velocity > 0.0001) {
      // Determine dominant direction
      if (absDx > absDy * 1.8) { // More strict horizontal axis
        previousHandsRef.current = [];
        return dx > 0 ? 'swipe_right' : 'swipe_left';
      } else if (absDy > absDx * 1.2) { // More tolerant vertical
        previousHandsRef.current = [];
        return dy > 0 ? 'swipe_down' : 'swipe_up';
      }
    }

    return null;
  }, []);

  const getStableGesture = useCallback((candidate) => {
    if (!candidate) return '';
    gestureHistoryRef.current.push(candidate);
    if (gestureHistoryRef.current.length > STABILITY_WINDOW) {
      gestureHistoryRef.current.shift();
    }

    const counts = gestureHistoryRef.current.reduce((acc, g) => {
      acc[g] = (acc[g] || 0) + 1;
      return acc;
    }, {});

    const topGesture = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    if (topGesture && topGesture[1] >= STABILITY_MIN_COUNT) {
      return topGesture[0];
    }
    return '';
  }, []);

  /**
   * Finger-State Engine - Calculates finger extension ratios
   */
  const getFingerStates = useCallback((landmarks) => {
    const fingers = [
      [5, 6, 7, 8],     // Index
      [9, 10, 11, 12],  // Middle
      [13, 14, 15, 16], // Ring
      [17, 18, 19, 20]  // Pinky
    ];

    const dist2D = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

    const scores = fingers.map(joints => {
      const segSum = dist2D(landmarks[joints[0]], landmarks[joints[1]]) +
        dist2D(landmarks[joints[1]], landmarks[joints[2]]) +
        dist2D(landmarks[joints[2]], landmarks[joints[3]]);
      const directDist = dist2D(landmarks[joints[0]], landmarks[joints[3]]);
      return directDist / (segSum || 1);
    });

    const palmScale = dist2D(landmarks[0], landmarks[9]);
    const thumbTip = landmarks[4];
    const indexMCP = landmarks[5];

    const thumbSegSum = dist2D(landmarks[1], landmarks[2]) + dist2D(landmarks[2], landmarks[3]) + dist2D(landmarks[3], landmarks[4]);
    const thumbDirect = dist2D(landmarks[1], landmarks[4]);
    const thumbScore = thumbDirect / (thumbSegSum || 1);

    const thumbTucked = dist2D(thumbTip, indexMCP) < palmScale * 0.6; // Increased tolerance

    return {
      indexScore: scores[0],
      middleScore: scores[1],
      ringScore: scores[2],
      pinkyScore: scores[3],
      thumbScore,
      thumbTucked
    };
  }, []);

  const detectGesture = useCallback((landmarks) => {
    const s = getFingerStates(landmarks);
    const threshold = 0.7; // Tighter extension requirement
    const bent = 0.45;    // Tighter fold requirement

    const thumbTip = landmarks[4];
    const thumbIP = landmarks[3];
    const indexMCP = landmarks[5];
    const allFingersBent = s.indexScore < bent && s.middleScore < bent && s.ringScore < bent && s.pinkyScore < bent;
    const thumbExtended = s.thumbScore > 0.75;

    // 1. Fist: High priority to prevent scrolling or point accidental triggers
    if (allFingersBent && (s.thumbTucked || s.thumbScore < 0.7)) {
      return 'fist';
    }

    // 2. Thumbs Up / Down: Increased threshold and required NOT tucked
    if (allFingersBent && thumbExtended && s.indexScore < 0.4 && !s.thumbTucked && s.thumbScore > 0.85) {
      if (thumbTip.y < thumbIP.y && thumbTip.y < indexMCP.y) {
        return 'thumbs_up';
      }
      if (thumbTip.y > thumbIP.y && thumbTip.y > indexMCP.y) {
        return 'thumbs_down';
      }
    }

    // 3. Rock On (🤘): Priority over Point/Peace
    if (s.indexScore > threshold && s.pinkyScore > threshold &&
      s.middleScore < bent && s.ringScore < bent) {
      return 'rock_on';
    }

    // 4. Peace (✌️): Priority over Point
    if (s.indexScore > threshold && s.middleScore > threshold && s.ringScore < bent && s.pinkyScore < bent) {
      return 'peace';
    }

    // 5. Point (☝️): Tightened to require pinky bent to avoid Rock On clash
    if (s.indexScore > threshold &&
      s.middleScore < bent &&
      s.ringScore < bent &&
      s.pinkyScore < bent) {
      // If thumb is tucked or way below index tip, it's a point
      if (s.thumbScore < 0.6 || thumbTip.y > landmarks[8].y + 0.1) {
        return 'point';
      }
    }

    return '';
  }, [getFingerStates]);


  const onResults = useCallback((results) => {
    if (!canvasRef.current || !videoRef.current) return;

    const canvasCtx = canvasRef.current.getContext('2d');
    const canvasWidth = canvasRef.current.width;
    const canvasHeight = canvasRef.current.height;

    canvasCtx.save();
    canvasCtx.clearRect(0, 0, canvasWidth, canvasHeight);

    // Draw camera feed onto canvas from the video element
    canvasCtx.drawImage(videoRef.current, 0, 0, canvasWidth, canvasHeight);

    if (results.multiHandLandmarks && results.multiHandLandmarks.length > 0) {
      let landmarks = results.multiHandLandmarks[0];
      landmarks = mirrorLandmarks(landmarks); // Apply mirroring
      landmarks = smoothLandmarks(landmarks);
      const detectedConfidence = results.multiHandedness?.[0]?.score ?? 1;


      if (detectedConfidence < minConfidenceRef.current) {
        canvasCtx.restore();
        return;
      }

      drawHandLandmarks(canvasCtx, landmarks);

      // 1. Calculate Velocity
      let currentVelocity = 0;
      if (previousLandmarksRef.current) {
        const dx = landmarks[9].x - previousLandmarksRef.current[9].x;
        const dy = landmarks[9].y - previousLandmarksRef.current[9].y;
        currentVelocity = Math.sqrt(dx * dx + dy * dy);
      }
      previousLandmarksRef.current = landmarks;

      // Update cooldown if moving
      if (currentVelocity > STATIC_GESTURE_MAX_SPEED) {
        movementCooldownRef.current = Date.now();
      }

      // 2. Priority: Check Swipes First (Bypass Stability)
      const swipe = detectSwipe(landmarks);
      if (swipe) {
        const lastGesture = lastGestureRef.current.gesture;
        const timeSinceLast = Date.now() - lastGestureRef.current.time;

        // 1. Check for opposite gesture lockout (prevent "ping-pong" effect)
        if (OPPOSITE_GESTURES[swipe] === lastGesture && timeSinceLast < 1500) {
          canvasCtx.restore();
          return;
        }

        // 2. Global Horizontal Swipe Cooldown (1200ms)
        const isHorizontal = swipe === 'swipe_left' || swipe === 'swipe_right';
        if (isHorizontal && timeSinceLast < 1200) {
          canvasCtx.restore();
          return;
        }

        if (onGestureRef.current) onGestureRef.current(swipe, landmarks[9]);
        lastGestureRef.current = { gesture: swipe, time: Date.now() };

        // Increased lockout for trailing static gestures (400ms)
        // Helps distinguish swipes from subsequent accidental hand extensions
        movementCooldownRef.current = Date.now() - 400;

        // Reset stability history to prevent trailing static gestures
        gestureHistoryRef.current = [];
        canvasCtx.restore();
        return;
      }

      // 3. Check Static Gestures (Only if hand is stable AND cooldown passed)
      const timeSinceMovement = Date.now() - movementCooldownRef.current;

      // Moderate cooldown (400ms) for deliberate static gesture response
      if (currentVelocity < STATIC_GESTURE_MAX_SPEED && timeSinceMovement > 400) {
        const detectedGesture = detectGesture(landmarks);

        const stableGesture = getStableGesture(detectedGesture);

        const now = Date.now();
        if (stableGesture === 'fist') {
          if (!fistHoldRef.current.start) {
            fistHoldRef.current = { start: now, emitted: false };
          }

          if (!fistHoldRef.current.emitted && now - fistHoldRef.current.start >= FIST_HOLD_MS) {
            if (onGestureRef.current) onGestureRef.current('fist_hold', landmarks[9]);
            fistHoldRef.current.emitted = true;
            lastGestureRef.current = { gesture: 'fist_hold', time: now };
          }
        } else if (stableGesture !== '') {
          // Only reset if we see a DIFFERENT specific gesture (like Point or Rock)
          // Allows 'fist_hold' to survive momentary '' (empty) detection frames
          fistHoldRef.current = { start: 0, emitted: false };

          if (
            stableGesture &&
            (stableGesture !== lastGestureRef.current.gesture || now - lastGestureRef.current.time > debounceMsRef.current)
          ) {
            if (onGestureRef.current) onGestureRef.current(stableGesture, landmarks[9]);
            lastGestureRef.current = { gesture: stableGesture, time: now };
          }
        }
      } else {
        // Hand is moving too fast for static gestures - clear history immediately
        gestureHistoryRef.current = [];
        movementCooldownRef.current = Date.now();
      }
    }

    canvasCtx.restore();
  }, [detectSwipe, drawHandLandmarks, getStableGesture, smoothLandmarks, mirrorLandmarks, detectGesture]);

  // Sync the global bridge for the singleton
  useEffect(() => {
    window.__MEDIAPIPE_RESULTS_LISTENER__ = (results) => {
      if (onResults) onResults(results);
    };
    return () => {
      window.__MEDIAPIPE_RESULTS_LISTENER__ = null;
    };
  }, [onResults]);

  useEffect(() => {
    if (!enabled) {
      if (cameraRef.current) {
        cameraRef.current.stop();
      }
      return;
    }

    setIsLoading(true);
    setError(null);

    getHandsInstance();

    if (videoRef.current) {
      const camera = new Camera(videoRef.current, {
        onFrame: async () => {
          if (videoRef.current && window.__MEDIAPIPE_HANDS_INSTANCE__) {
            try {
              await window.__MEDIAPIPE_HANDS_INSTANCE__.send({ image: videoRef.current });
            } catch (e) {
              if (!e.message?.includes('Module.arguments')) {
                console.error("MediaPipe error:", e);
              }
            }
          }
        },
        width: 320,
        height: 240
      });

      camera.start()
        .then(() => {
          setIsLoading(false);
          cameraRef.current = camera;
        })
        .catch((err) => {
          setError('Camera access denied. Please allow camera permissions.');
          setIsLoading(false);
          console.error('Camera error:', err);
        });
    }

    return () => {
      if (cameraRef.current) {
        cameraRef.current.stop();
        cameraRef.current = null;
      }
    };
  }, [enabled]); // Only re-run when enabled/disabled


  return (
    <div className="gesture-control">
      <button
        className="toggle-gesture-btn"
        onClick={() => onEnabledChange(!enabled)}
      >
        {enabled ? '🎥 Disable Gestures' : '🎥 Enable Gestures'}
      </button>

      {enabled && (
        <div className="gesture-side-panel">
          <div className="panel-header">
            <h3>🎥 Gesture Control</h3>
            <button
              className="close-panel-btn"
              onClick={() => onEnabledChange(false)}
              title="Close (or hold fist for 2s)"
            >
              ✕
            </button>
          </div>

          <div className="panel-content">
            <div className="camera-section">
              <video
                ref={videoRef}
                style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                autoPlay
                playsInline
                muted
              />
              <canvas
                ref={canvasRef}
                className="canvas-feed"
                width="320"
                height="240"
              />
              {isLoading && <div className="loading">Initializing...</div>}
              {error && <div className="error">{error}</div>}
            </div>


            <div className="gestures-section">
              <div className="section-label">Gestures</div>
              <div className="gestures-compact">
                <div className="gesture-compact-item">👈 Swipe Prev</div>
                <div className="gesture-compact-item">👉 Swipe Next</div>
                <div className="gesture-compact-item">🤘 Rock: Open / Select</div>
                <div className="gesture-compact-item">👍 Scroll Up</div>
                <div className="gesture-compact-item">👎 Scroll Down</div>
                <div className="gesture-compact-item">✌️ Home</div>
                <div className="gesture-compact-item">☝️ Mode Toggle</div>
                <div className="gesture-compact-item">✊ Hold 2s: Stop</div>
              </div>
            </div>

            <div className="controls-section">
              <div className="section-label">Settings</div>

              <label className="compact-slider">
                <span className="slider-name">Swipe</span>
                <input
                  type="range"
                  min="0.05"
                  max="0.2"
                  step="0.01"
                  value={swipeThreshold}
                  onChange={(e) => setSwipeThreshold(Number(e.target.value))}
                />
                <span className="slider-val">{swipeThreshold.toFixed(2)}</span>
              </label>

              <label className="compact-slider">
                <span className="slider-name">Delay</span>
                <input
                  type="range"
                  min="300"
                  max="1200"
                  step="50"
                  value={debounceMs}
                  onChange={(e) => setDebounceMs(Number(e.target.value))}
                />
                <span className="slider-val">{debounceMs}ms</span>
              </label>

              <label className="compact-slider">
                <span className="slider-name">MinConf</span>
                <input
                  type="range"
                  min="0.3"
                  max="0.9"
                  step="0.01"
                  value={minConfidence}
                  onChange={(e) => setMinConfidence(Number(e.target.value))}
                />
                <span className="slider-val">{minConfidence.toFixed(2)}</span>
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestureControl;
